import { Component } from '@theme/component';

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_SUBSCRIBE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
const SUBSCRIPTION_SOURCE = 'Back in Stock';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i;
const PHONE_DISALLOWED = /[^\d+()\-.\s]/g;

/**
 * "Email when available" modal. Subscribes the shopper to a Klaviyo back in
 * stock alert for a variant, by email or SMS. Klaviyo's Back in Stock flow
 * sends the actual restock notification.
 *
 * The alert alone records no consent. SMS needs SMS consent to be sent at all,
 * so an SMS signup always also subscribes the number to `data-sms-list-id`.
 * An email signup subscribes to `data-email-list-id` only when the shopper
 * keeps the opt-in checked. Without a list id that subscription is skipped.
 *
 * The product page renders one per page with its variant in `data-variant-id`.
 * The global instance (`data-global`, rendered in the layout) is opened by any
 * `[data-back-in-stock-trigger]` element, which supplies the variant and the
 * product title.
 *
 * @typedef {Object} BackInStockModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} header
 * @property {HTMLFormElement} form
 * @property {HTMLElement} formView
 * @property {HTMLElement} successView
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} emailGroup
 * @property {HTMLInputElement} emailInput
 * @property {HTMLElement} emailError
 * @property {HTMLInputElement} [emailOptin]
 * @property {HTMLElement} [phoneGroup]
 * @property {HTMLInputElement} [phoneInput]
 * @property {HTMLElement} [phoneError]
 * @property {HTMLButtonElement[]} [channelButtons]
 * @property {HTMLElement[]} [productNames]
 * @property {HTMLElement} status
 * @property {HTMLButtonElement} submitButton
 *
 * @extends {Component<BackInStockModalRefs>}
 */
class BackInStockModal extends Component {
  requiredRefs = [
    'dialog',
    'header',
    'form',
    'formView',
    'successView',
    'successMessage',
    'emailGroup',
    'emailInput',
    'emailError',
    'status',
    'submitButton',
  ];

  /** @type {'EMAIL' | 'SMS'} */
  #channel = 'EMAIL';

  /** @type {string} */
  #variantId = '';

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    // Registered natively: the declarative `on:click` proxy rewrites `target`,
    // which hides whether the click landed on the backdrop.
    this.refs.dialog.addEventListener('click', this.#handleBackdropClick, {
      signal: this.#abortController.signal,
    });

    if (this.hasAttribute('data-global')) {
      document.addEventListener('click', this.#handleTriggerClick, {
        signal: this.#abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  open() {
    this.#variantId = this.dataset.variantId ?? '';
    this.#show();
  }

  /**
   * @param {string} variantId
   * @param {string} productTitle
   */
  openFor(variantId, productTitle) {
    this.#variantId = variantId;
    for (const name of this.refs.productNames ?? []) name.textContent = productTitle;
    this.#show();
  }

  #show() {
    this.#reset();
    this.refs.dialog.showModal();
    this.#activeInput.focus();
  }

  close() {
    this.refs.dialog.close();
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const trigger = /** @type {HTMLElement | null} */ (target?.closest?.('[data-back-in-stock-trigger]') ?? null);
    if (!trigger?.dataset.variantId) return;

    event.preventDefault();
    this.openFor(trigger.dataset.variantId, trigger.dataset.productTitle ?? '');
  };

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  /** @param {Event} event */
  selectChannel(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const channel = button.dataset.channel === 'SMS' ? 'SMS' : 'EMAIL';
    if (channel === this.#channel) return;

    this.#channel = channel;
    for (const channelButton of this.refs.channelButtons ?? []) {
      channelButton.setAttribute('aria-pressed', String(channelButton.dataset.channel === channel));
    }
    this.refs.emailGroup.hidden = channel !== 'EMAIL';
    if (this.refs.phoneGroup) this.refs.phoneGroup.hidden = channel !== 'SMS';
    this.#setError('');
    this.#clearFieldErrors();
  }

  /** Validates once the shopper leaves a field they typed in. @param {FocusEvent} event */
  handleFieldBlur(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.value.trim()) return;
    this.#validateField(input);
  }

  /** Clears the error as soon as the value becomes valid. @param {InputEvent} event */
  handleFieldInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input === this.refs.phoneInput) {
      const cleaned = input.value.replace(PHONE_DISALLOWED, '');
      if (cleaned !== input.value) input.value = cleaned;
    }
    if (input.getAttribute('aria-invalid') === 'true') this.#validateField(input);
  }

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();

    const profile = this.#buildProfile();
    if (!profile) return;

    this.#setError('');
    this.#setBusy(true);

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId ?? '')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: this.dataset.revision ?? '2024-10-15',
        },
        body: JSON.stringify(this.#buildPayload(profile)),
      });

      if (!response.ok) throw new Error(await this.#readError(response));

      // The alert is registered, so a failed subscription still shows success.
      await this.#subscribe(profile).catch((error) => console.error('[back-in-stock] subscription', error));

      this.refs.formView.hidden = true;
      this.refs.header.hidden = true;
      this.refs.successView.hidden = false;
      this.refs.successMessage.focus();
    } catch (error) {
      console.error('[back-in-stock]', error);
      this.#setError(this.dataset.errorGeneric ?? '');
    } finally {
      this.#setBusy(false);
    }
  }

  get #activeInput() {
    return this.#channel === 'SMS' && this.refs.phoneInput ? this.refs.phoneInput : this.refs.emailInput;
  }

  /** @returns {{ email?: string, phone_number?: string } | null} */
  #buildProfile() {
    const input = this.#activeInput;
    if (!this.#validateField(input)) {
      input.focus();
      return null;
    }

    if (input === this.refs.phoneInput) return { phone_number: this.#formatPhone(input.value) ?? '' };
    return { email: input.value.trim() };
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {boolean} whether the value is valid
   */
  #validateField(input) {
    const isPhone = input === this.refs.phoneInput;
    const valid = isPhone ? Boolean(this.#formatPhone(input.value)) : EMAIL_PATTERN.test(input.value.trim());
    const errorText = isPhone ? this.refs.phoneError : this.refs.emailError;
    const message = isPhone ? this.dataset.errorPhone : this.dataset.errorEmail;

    if (valid) input.removeAttribute('aria-invalid');
    else input.setAttribute('aria-invalid', 'true');
    if (errorText) {
      errorText.textContent = valid ? '' : message ?? '';
      /** @type {HTMLElement} */ (errorText.parentElement).hidden = valid;
    }
    return valid;
  }

  #clearFieldErrors() {
    for (const [input, errorText] of [
      [this.refs.emailInput, this.refs.emailError],
      [this.refs.phoneInput, this.refs.phoneError],
    ]) {
      input?.removeAttribute('aria-invalid');
      if (errorText) {
        errorText.textContent = '';
        /** @type {HTMLElement} */ (errorText.parentElement).hidden = true;
      }
    }
  }

  /**
   * Records marketing consent: always for SMS, for email only when opted in.
   * @param {{ email?: string, phone_number?: string }} profile
   */
  async #subscribe(profile) {
    const isSms = this.#channel === 'SMS';
    if (!isSms && !this.refs.emailOptin?.checked) return;

    const listId = isSms ? this.dataset.smsListId : this.dataset.emailListId;
    if (!listId) return;

    const channel = isSms ? 'sms' : 'email';
    const response = await fetch(
      `${KLAVIYO_SUBSCRIBE_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId ?? '')}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          revision: this.dataset.revision ?? '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: SUBSCRIPTION_SOURCE,
              profile: {
                data: {
                  type: 'profile',
                  attributes: { ...profile, subscriptions: { [channel]: { marketing: { consent: 'SUBSCRIBED' } } } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      }
    );

    if (!response.ok) throw new Error(await this.#readError(response));
  }

  /** @param {{ email?: string, phone_number?: string }} profile */
  #buildPayload(profile) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: [this.#channel],
          profile: { data: { type: 'profile', attributes: profile } },
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.#variantId}` },
          },
        },
      },
    };
  }

  /**
   * Normalizes to E.164. Ten digits are assumed to be a US number.
   * @param {string} value
   * @returns {string | null}
   */
  #formatPhone(value) {
    const digits = value.replace(/\D/g, '');
    if (value.trim().startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return null;
  }

  /** @param {Response} response */
  async #readError(response) {
    try {
      const json = await response.json();
      return json?.errors?.[0]?.detail ?? `HTTP ${response.status}`;
    } catch {
      return `HTTP ${response.status}`;
    }
  }

  /** Form-level error (e.g. the request failed). @param {string} message */
  #setError(message) {
    this.refs.status.textContent = message;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.refs.submitButton.disabled = busy;
    this.refs.submitButton.setAttribute('aria-busy', String(busy));
  }

  #reset() {
    this.refs.form.reset();
    this.refs.formView.hidden = false;
    this.refs.header.hidden = false;
    this.refs.successView.hidden = true;
    this.#setError('');
    this.#clearFieldErrors();
  }
}

if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', BackInStockModal);
}
